export interface EmailRenderOptions {
  sections: any[];
  businessName: string;
  logoUrl?: string;
  variables?: Record<string, string>;
}

function applyVariables(text: string, variables?: Record<string, string>): string {
  if (!variables || !text) return text || '';
  let result = text;
  for (const [key, value] of Object.entries(variables)) {
    result = result.split(key).join(value);
  }
  return result;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');
}

// Wraps content in a full-width table row with bgcolor for Gmail dark mode compatibility
function tableRow(bgColor: string, padding: string, align: string, inner: string): string {
  return `<table width="100%" border="0" cellpadding="0" cellspacing="0" bgcolor="${bgColor}" style="background-color:${bgColor};"><tr><td align="${align}" style="padding:${padding};background-color:${bgColor};">${inner}</td></tr></table>`;
}

function renderSection(section: any, businessName: string, logoUrl?: string, variables?: Record<string, string>): string {
  const { type, content } = section;
  if (!type) return '';

  switch (type) {
    case 'header': {
      const bgColor = content?.backgroundColor || '#6366f1';
      const textColor = content?.textColor || '#ffffff';
      const padding = content?.padding || '30px';
      const displayMode = content?.displayMode || (content?.logo ? 'logo' : 'name');
      const logoSize = content?.logoSize || 80;
      const logoColorMode = content?.logoColorMode || (content?.logoInvert ? 'white' : 'original');

      let headerInner = '';
      if (displayMode === 'logo' && logoUrl) {
        const effectiveUrl = (logoColorMode !== 'original' && content?.processedLogoUrl) ? content.processedLogoUrl : logoUrl;
        headerInner = `<img src="${effectiveUrl}" alt="${businessName}" width="${logoSize}" style="height:${logoSize}px;width:auto;max-width:100%;object-fit:contain;display:inline-block;" />`;
      } else {
        headerInner = `<span style="color:${textColor};font-size:28px;font-weight:bold;letter-spacing:1px;text-transform:uppercase;">${businessName}</span>`;
      }

      return tableRow(bgColor, padding, 'center', headerInner);
    }

    case 'hero': {
      const bgColor = content?.backgroundColor || '#f3f4f6';
      const parts: string[] = [];

      if (content?.image) {
        parts.push(`<img src="${content.image}" style="max-width:100%;height:auto;margin-bottom:20px;border-radius:4px;" alt="Hero Image" />`);
      }
      if (content?.heading) {
        parts.push(`<h1 style="font-size:28px;margin:0 0 16px 0;color:#1f2937;line-height:1.3;font-family:Arial,sans-serif;">${applyVariables(content.heading, variables)}</h1>`);
      }
      if (content?.subheading) {
        parts.push(`<p style="font-size:18px;color:#6b7280;margin:0 0 20px 0;line-height:1.5;font-family:Arial,sans-serif;">${applyVariables(content.subheading, variables)}</p>`);
      }
      if (content?.buttonText) {
        const buttonUrl = content?.buttonUrl || '#';
        const btnBg = content?.buttonBackgroundColor || content?.buttonColorBg || '#6366f1';
        const btnText = content?.buttonColor || '#ffffff';
        parts.push(`<table border="0" cellpadding="0" cellspacing="0" style="margin:20px auto;"><tr><td bgcolor="${btnBg}" style="padding:12px 24px;border-radius:6px;background-color:${btnBg};"><a href="${buttonUrl}" target="_blank" style="color:${btnText};display:inline-block;font-family:Arial,sans-serif;font-size:16px;font-weight:bold;line-height:20px;text-decoration:none;">${content.buttonText}</a></td></tr></table>`);
      }

      return tableRow(bgColor, '40px', 'center', parts.join(''));
    }

    case 'text': {
      const text = typeof content === 'string' ? content : content?.text;
      if (!text) return '';
      const align = content?.align || 'left';
      const fontSize = content?.fontSize || '16px';
      const color = content?.color || '#374151';
      const isRichHtml = /<[a-z][\s\S]*>/i.test(text);
      const textContent = isRichHtml ? applyVariables(text, variables) : escapeHtml(applyVariables(text, variables));
      const inner = `<div style="font-size:${fontSize};line-height:1.6;color:${color};font-family:Arial,sans-serif;">${textContent}</div>`;
      return tableRow('#ffffff', '20px', align, inner);
    }

    case 'image': {
      const url = content?.url || content?.imageUrl;
      if (!url) return '';
      const alt = content?.alt || content?.altText || 'Email Image';
      const width = content?.width || '100%';
      const align = content?.align || 'center';
      const inner = `<img src="${url}" style="max-width:${width};height:auto;border-radius:4px;" alt="${alt}" />`;
      return tableRow('#ffffff', '20px', align, inner);
    }

    case 'button': {
      if (!content?.text) return '';
      const buttonUrl = content?.url || '#';
      const bgColor = content?.backgroundColor || '#6366f1';
      const btnTextColor = content?.color || '#ffffff';
      const inner = `<table border="0" cellpadding="0" cellspacing="0" style="margin:0 auto;"><tr><td bgcolor="${bgColor}" style="padding:12px 24px;border-radius:6px;background-color:${bgColor};"><a href="${buttonUrl}" target="_blank" style="color:${btnTextColor};display:inline-block;font-family:Arial,sans-serif;font-size:16px;font-weight:bold;line-height:20px;text-decoration:none;">${content.text}</a></td></tr></table>`;
      return tableRow('#ffffff', '20px', content?.align || 'center', inner);
    }

    case 'divider': {
      const color = content?.color || '#e5e7eb';
      const thickness = content?.thickness || '1px';
      const inner = `<hr style="border:none;border-top:${thickness} solid ${color};margin:0;" />`;
      return tableRow('#ffffff', '10px 20px', 'left', inner);
    }

    case 'spacer': {
      const height = content?.height || 20;
      return `<table width="100%" border="0" cellpadding="0" cellspacing="0" bgcolor="#ffffff" style="background-color:#ffffff;"><tr><td style="height:${height}px;background-color:#ffffff;font-size:0;line-height:0;">&nbsp;</td></tr></table>`;
    }

    case 'card': {
      const cardBg = content?.backgroundColor || '#f9fafb';
      const borderColor = content?.borderColor || '#e5e7eb';
      const title = applyVariables(content?.title || '', variables);
      const cardContent = applyVariables(content?.content || '', variables);
      const inner = `<table width="100%" border="0" cellpadding="20" cellspacing="0" style="background-color:${cardBg};border:1px solid ${borderColor};border-radius:8px;" bgcolor="${cardBg}"><tr><td><h3 style="font-size:20px;font-weight:bold;margin:0 0 12px 0;color:#1f2937;font-family:Arial,sans-serif;">${title}</h3><p style="color:#6b7280;margin:0;font-family:Arial,sans-serif;">${cardContent}</p></td></tr></table>`;
      return tableRow('#ffffff', '20px', 'left', inner);
    }

    case 'footer': {
      const bgColor = content?.backgroundColor || '#f9fafb';
      const footerColor = content?.color || '#6b7280';
      const footerText = applyVariables(content?.text || '', variables);
      const inner = `<span style="color:${footerColor};font-size:14px;font-family:Arial,sans-serif;">${footerText}</span>`;
      return tableRow(bgColor, '20px', 'center', inner);
    }

    case 'html': {
      const raw = typeof content === 'string' ? content : content?.html || '';
      return raw ? tableRow('#ffffff', '20px', 'left', applyVariables(raw, variables)) : '';
    }

    default:
      return '';
  }
}

export function renderSectionsToHtml(options: EmailRenderOptions): string {
  const { sections, businessName, logoUrl, variables } = options;
  return sections
    .map((section) => renderSection(section, businessName, logoUrl, variables))
    .filter((html) => html.trim())
    .join('');
}

/**
 * Renders a complete HTML email using table-based layout for maximum
 * email client compatibility (including Gmail dark mode).
 */
export function renderEmailTemplateToHtml(options: EmailRenderOptions): string {
  const sectionsHtml = renderSectionsToHtml(options);
  return [
    '<!DOCTYPE html>',
    '<html xmlns="http://www.w3.org/1999/xhtml">',
    '<head>',
    '<meta charset="UTF-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1.0">',
    '<meta name="color-scheme" content="light">',
    '<meta name="supported-color-schemes" content="light">',
    '<!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->',
    '</head>',
    '<body style="margin:0;padding:0;background-color:#f9fafb;font-family:Arial,sans-serif;" bgcolor="#f9fafb">',
    '<table width="100%" border="0" cellpadding="0" cellspacing="0" bgcolor="#f9fafb" style="background-color:#f9fafb;">',
    '<tr><td align="center" style="padding:20px;">',
    '<table width="600" border="0" cellpadding="0" cellspacing="0" bgcolor="#ffffff" style="background-color:#ffffff;border-radius:8px;overflow:hidden;max-width:600px;width:100%;">',
    '<tr><td>',
    sectionsHtml,
    '</td></tr>',
    '</table>',
    '</td></tr>',
    '</table>',
    '</body></html>',
  ].join('');
}
